import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import {
  MessageSquare, Search, RefreshCw, Send,
  CheckCircle, Archive, Building2, ShoppingBag, ArrowLeft
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { GET_TICKETS, UPDATE_TICKET, ADD_MESSAGE_TO_TICKET, MARK_TICKET_READ, CREATE_TICKET } from '../../api/messages-api';

const TAB_ALL = 'all';
const TAB_LAB = 'LABORATORY';
const TAB_SELLER = 'SELLER_PHARMACIST';
const TAB_ARCHIVED = 'ARCHIVED';

const getStatusColor = (status) => {
  switch (status) {
    case 'OPEN': return 'bg-blue-100 text-blue-700';
    case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-700';
    case 'RESOLVED': return 'bg-green-100 text-green-700';
    case 'CLOSED': return 'bg-gray-100 text-gray-500';
    default: return 'bg-gray-100 text-gray-500';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'OPEN': return 'Ouvert';
    case 'IN_PROGRESS': return 'En cours';
    case 'RESOLVED': return 'Résolu';
    case 'CLOSED': return 'Archivé';
    default: return status;
  }
};

const getUserTypeInfo = (userType) => {
  switch (userType) {
    case 'PHARMACIST': return { label: 'Pharmacien', colorClass: 'bg-emerald-100 text-emerald-700' };
    case 'LABORATORY': return { label: 'Laboratoire', colorClass: 'bg-blue-100 text-blue-700' };
    case 'SELLER': return { label: 'Vendeur', colorClass: 'bg-purple-100 text-purple-700' };
    default: return { label: userType || '—', colorClass: 'bg-gray-100 text-gray-600' };
  }
};

const Messages = () => {
  const [searchParams] = useSearchParams();

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(TAB_ALL);
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'conversation'

  // Optimistic messages: list of locally-appended admin replies pending server confirm
  const [pendingMessages, setPendingMessages] = useState([]);

  const messagesEndRef = useRef(null);
  const replyInputRef = useRef(null);

  const [isRefreshing, setIsRefreshing] = useState(false);
  // Locally track which tickets have been "read" by the admin clicking them
  const [readTicketIds, setReadTicketIds] = useState(new Set());
  const [newConvInput, setNewConvInput] = useState('');

  const { data: ticketsData, loading: ticketsLoading, refetch: refetchTickets } = useQuery(GET_TICKETS, {
    variables: { filters: null },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try { await refetchTickets(); } finally { setIsRefreshing(false); }
  };

  const [markTicketRead] = useMutation(MARK_TICKET_READ);

  const [updateTicket] = useMutation(UPDATE_TICKET, {
    onCompleted: async () => {
      const result = await refetchTickets();
      if (result?.data?.tickets && selectedTicket) {
        const updated = result.data.tickets.find(t => t.id === selectedTicket.id);
        if (updated) setSelectedTicket(updated);
      }
    },
  });

  const [addMessageToTicket, { loading: sendingReply }] = useMutation(ADD_MESSAGE_TO_TICKET, {
    onCompleted: async () => {
      setPendingMessages([]);
      setReplyText('');
      const result = await refetchTickets();
      // Update the selected ticket with fresh data from refetch
      if (result?.data?.tickets && selectedTicket) {
        const updated = result.data.tickets.find(t => t.id === selectedTicket.id);
        if (updated) setSelectedTicket(updated);
      }
    },
    onError: (error) => {
      setPendingMessages([]);
      toast.error(`Erreur lors de l'envoi: ${error.message}`);
    }
  });

  const [createTicket, { loading: creatingTicket }] = useMutation(CREATE_TICKET, {
    onCompleted: async (data) => {
      const newTicket = data.createTicket;
      const result = await refetchTickets();
      if (result?.data?.tickets) {
        const found = result.data.tickets.find(t => t.id === newTicket.id);
        if (found) setSelectedTicket(found);
      }
      setNewConvInput('');
    },
    onError: (error) => {
      toast.error(`Erreur lors de la création: ${error.message}`);
    }
  });

  const allTickets = ticketsData?.tickets || [];

  // Auto-select ticket from query param (e.g. ?userId=X coming from lab/users pages)
  const autoSelectedRef = useRef(null);
  useEffect(() => {
    const targetUserId = searchParams.get('userId');
    if (!targetUserId) return;
    if (autoSelectedRef.current === targetUserId) return;
    if (allTickets.length === 0) return;

    const match = allTickets
      .filter(t => t.userId === targetUserId || t.user?.id === targetUserId)
      .sort((a, b) => new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt))[0];

    autoSelectedRef.current = targetUserId;
    if (match) {
      setSelectedTicket(match);
    } else {
      setSelectedTicket(null);
    }
  }, [allTickets, searchParams]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket, pendingMessages]);

  useEffect(() => {
    if (selectedTicket && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [selectedTicket]);

  const isArchived = (t) => t.status === 'CLOSED';
  const isActive = (t) => t.status !== 'CLOSED';

  const filteredTickets = allTickets
    .filter((ticket) => {
      const matchesSearch =
        (ticket.user?.firstName + ' ' + ticket.user?.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ticket.firstMessage?.content || '').toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;

      if (activeTab === TAB_ARCHIVED) return isArchived(ticket);
      if (!isActive(ticket)) return false; // hide archived from non-archived tabs
      if (activeTab === TAB_LAB) return ticket.user?.userType === 'LABORATORY';
      if (activeTab === TAB_SELLER) return ticket.user?.userType === 'SELLER' || ticket.user?.userType === 'PHARMACIST';
      return true; // TAB_ALL
    })
    .sort((a, b) => new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt));

  const counts = {
    all: allTickets.filter(isActive).length,
    lab: allTickets.filter(t => isActive(t) && t.user?.userType === 'LABORATORY').length,
    seller: allTickets.filter(t => isActive(t) && (t.user?.userType === 'SELLER' || t.user?.userType === 'PHARMACIST')).length,
    archived: allTickets.filter(isArchived).length,
  };

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setPendingMessages([]);
    setReplyText('');
    setMobileView('conversation');
    if (ticket.hasUnreadMessages) {
      setReadTicketIds(prev => new Set([...prev, ticket.id]));
      markTicketRead({ variables: { id: ticket.id } });
    }
  };

  const handleStartNewConversation = (e) => {
    e?.preventDefault();
    const targetUserId = searchParams.get('userId');
    if (!newConvInput.trim() || !targetUserId || creatingTicket) return;
    createTicket({
      variables: {
        createTicketInput: {
          subject: 'Support Mazid',
          content: newConvInput.trim(),
          category: 'GENERAL',
          priority: 'NORMAL',
          userId: targetUserId
        }
      }
    });
  };

  const handleUpdateStatus = (status) => {
    if (!selectedTicket) return;
    updateTicket({ variables: { updateTicketInput: { id: selectedTicket.id, status } } });
    setSelectedTicket(prev => ({ ...prev, status }));
  };

  const handleSubmitReply = (e) => {
    e?.preventDefault();
    if (!replyText.trim() || !selectedTicket || sendingReply) return;

    // Optimistic update — add message locally immediately
    const optimistic = {
      id: `pending-${Date.now()}`,
      content: replyText,
      adminReply: replyText,
      date: new Date().toISOString(),
      user: { firstName: 'Admin', lastName: 'Mazid' }
    };
    setPendingMessages(prev => [...prev, optimistic]);

    addMessageToTicket({
      variables: {
        addMessageInput: {
          ticketId: selectedTicket.id,
          content: replyText,
          adminReply: replyText,
          userId: selectedTicket.userId
        }
      }
    });
  };

  // Merge server messages + pending optimistic ones
  const allMessages = selectedTicket
    ? [...(selectedTicket.messages || []), ...pendingMessages]
    : [];

  const TABS = [
    { key: TAB_ALL, label: 'Tous', count: counts.all },
    { key: TAB_LAB, label: 'Laboratoires', count: counts.lab, icon: Building2 },
    { key: TAB_SELLER, label: 'Vendeurs & Pharm.', count: counts.seller, icon: ShoppingBag },
    { key: TAB_ARCHIVED, label: 'Archivés', count: counts.archived, icon: Archive },
  ];

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 4rem)', overflow: 'hidden' }}>
      {/* Page Header */}
      <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
            <MessageSquare size={16} className="text-green-700" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-gray-900">Support</h1>
            <p className="text-xs text-gray-400">{counts.all} ticket(s) actif(s)</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw size={12} className={`mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Actualisation...' : 'Actualiser'}
        </button>
      </div>

      {/* Main layout */}
      <div className="flex flex-1 min-h-0">

        {/* Left panel — full width on mobile (list view), w-80 on desktop */}
        <div className={`${mobileView === 'conversation' ? 'hidden sm:flex' : 'flex'} sm:flex w-full sm:w-80 flex-shrink-0 border-r border-gray-200 flex-col bg-white min-h-0`}>
          {/* Search */}
          <div className="flex-shrink-0 p-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex-shrink-0 flex border-b border-gray-100 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-shrink-0 py-2.5 px-3 text-xs font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'text-green-700 border-b-2 border-green-700 -mb-px bg-green-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.label}
                <span className="ml-1 text-gray-400">({tab.count})</span>
              </button>
            ))}
          </div>

          {/* Ticket list */}
          <div className="flex-1 overflow-y-auto">
            {ticketsLoading ? (
              <div className="flex justify-center py-12">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-green-700 border-t-transparent" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="py-12 text-center text-gray-400">
                <MessageSquare size={28} className="mx-auto mb-2 opacity-40" />
                <p className="text-xs">Aucun ticket</p>
              </div>
            ) : (
              filteredTickets.map(ticket => {
                const typeInfo = getUserTypeInfo(ticket.user?.userType);
                const isSelected = selectedTicket?.id === ticket.id;
                return (
                  <div
                    key={ticket.id}
                    onClick={() => handleSelectTicket(ticket)}
                    className={`px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors ${
                      isSelected ? 'bg-green-50 border-l-2 border-l-green-600' : 'hover:bg-gray-50'
                    } ${ticket.hasUnreadMessages && !readTicketIds.has(ticket.id) ? 'bg-blue-50 hover:bg-blue-50' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center space-x-1.5 min-w-0">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${typeInfo.colorClass}`}>
                          {typeInfo.label}
                        </span>
                        <span className="text-sm font-medium text-gray-800 truncate">
                          {ticket.user?.firstName} {ticket.user?.lastName}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0 ml-1">
                        {ticket.hasUnreadMessages && !readTicketIds.has(ticket.id) && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                        <span className="text-xs text-gray-400">
                          {format(new Date(ticket.lastMessageAt || ticket.createdAt), 'dd MMM', { locale: fr })}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 truncate mb-1.5">
                      {ticket.firstMessage?.content || ticket.subject}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(ticket.status)}`}>
                      {getStatusLabel(ticket.status)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right panel — hidden on mobile unless conversation selected */}
        <div className={`${mobileView === 'list' ? 'hidden sm:flex' : 'flex'} flex-1 flex-col bg-gray-50 min-h-0`}>
          {selectedTicket ? (
            <>
              {/* Ticket header */}
              <div className="flex-shrink-0 px-3 sm:px-5 py-3 bg-white border-b border-gray-200">
                <div className="flex items-center justify-between gap-2">
                  {/* Back button on mobile */}
                  <button
                    onClick={() => setMobileView('list')}
                    className="sm:hidden flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                    aria-label="Retour"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2 mb-0.5">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${getUserTypeInfo(selectedTicket.user?.userType).colorClass}`}>
                        {getUserTypeInfo(selectedTicket.user?.userType).label}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {selectedTicket.user?.firstName} {selectedTicket.user?.lastName}
                      </span>
                    </div>
                    <div className="flex items-center flex-wrap gap-x-3 text-xs text-gray-400">
                      <span>{selectedTicket.user?.email}</span>
                      {selectedTicket.user?.userType === 'PHARMACIST' && selectedTicket.user?.pharmacy && (
                        <span className="text-emerald-600">{selectedTicket.user.pharmacy.namePharmacy}</span>
                      )}
                      {selectedTicket.user?.userType === 'LABORATORY' && selectedTicket.user?.ownedLaboratory && (
                        <span className="text-blue-600">{selectedTicket.user.ownedLaboratory.nom}</span>
                      )}
                      <span>Ouvert le {format(new Date(selectedTicket.createdAt), 'dd MMM yyyy', { locale: fr })}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(selectedTicket.status)}`}>
                      {getStatusLabel(selectedTicket.status)}
                    </span>
                    {selectedTicket.status !== 'RESOLVED' && selectedTicket.status !== 'CLOSED' && (
                      <button
                        onClick={() => handleUpdateStatus('RESOLVED')}
                        className="flex items-center px-2.5 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                      >
                        <CheckCircle size={12} className="mr-1" />
                        Résoudre
                      </button>
                    )}
                    {selectedTicket.status !== 'CLOSED' && (
                      <button
                        onClick={() => handleUpdateStatus('CLOSED')}
                        className="flex items-center px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <Archive size={12} className="mr-1" />
                        Archiver
                      </button>
                    )}
                    {selectedTicket.status === 'CLOSED' && (
                      <button
                        onClick={() => handleUpdateStatus('OPEN')}
                        className="flex items-center px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                      >
                        Rouvrir
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages — only this scrolls */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {allMessages.length > 0 ? (
                  allMessages.map((message) => (
                    <div key={message.id} className="space-y-2">
                      {/* User message — only show when it's NOT an admin reply */}
                      {message.content && !message.adminReply && (
                        <div className="flex justify-end">
                          <div className="max-w-xs sm:max-w-lg">
                            <div className="flex items-center justify-end space-x-1.5 mb-1">
                              <span className="text-xs text-gray-400">
                                {format(new Date(message.date), 'dd MMM, HH:mm', { locale: fr })}
                              </span>
                              <span className="text-xs font-medium text-gray-600">
                                {message.user?.firstName} {message.user?.lastName}
                              </span>
                            </div>
                            <div dir="auto" className="px-4 py-2.5 rounded-2xl rounded-tr-sm bg-white border border-gray-200 shadow-sm text-sm text-gray-800 whitespace-pre-line">
                              {message.content}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Admin reply */}
                      {message.adminReply && (
                        <div className="flex justify-start">
                          <div className="max-w-xs sm:max-w-lg">
                            <div className="flex items-center space-x-1.5 mb-1">
                              <span className="text-xs font-medium text-green-700">Admin Mazid</span>
                              <span className="text-xs text-gray-400">
                                {format(new Date(message.date), 'dd MMM, HH:mm', { locale: fr })}
                              </span>
                              {message.id.startsWith('pending-') && (
                                <span className="text-xs text-gray-400 italic">envoi...</span>
                              )}
                            </div>
                            <div dir="auto" className="px-4 py-2.5 rounded-2xl rounded-tl-sm bg-green-700 text-white text-sm whitespace-pre-line">
                              {message.adminReply}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400 text-sm">Aucun message</div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply input */}
              {selectedTicket.status !== 'CLOSED' && (
                <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
                  <form onSubmit={handleSubmitReply} className="flex items-end space-x-2">
                    <textarea
                      ref={replyInputRef}
                      rows={2}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmitReply();
                        }
                      }}
                      placeholder="Répondre... (Entrée pour envoyer, Maj+Entrée pour nouvelle ligne)"
                      className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <button
                      type="submit"
                      disabled={!replyText.trim() || sendingReply}
                      className="flex-shrink-0 p-2.5 rounded-xl bg-green-700 text-white hover:bg-green-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {sendingReply
                        ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        : <Send size={16} />}
                    </button>
                  </form>
                </div>
              )}
            </>
          ) : searchParams.get('userId') && autoSelectedRef.current === searchParams.get('userId') ? (
            /* Admin-initiated conversation: user hasn't contacted support yet */
            <div className="flex flex-col h-full">
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mb-4">
                  <MessageSquare size={24} className="text-green-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Nouvelle conversation</h3>
                <p className="text-xs text-gray-400 max-w-xs">
                  Cet utilisateur n'a pas encore contacté le support. Envoyez-lui un premier message.
                </p>
              </div>
              <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
                <form onSubmit={handleStartNewConversation} className="flex items-center space-x-2">
                  <input
                    autoFocus
                    type="text"
                    value={newConvInput}
                    onChange={(e) => setNewConvInput(e.target.value)}
                    placeholder="Écrivez votre message..."
                    className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={creatingTicket}
                  />
                  <button
                    type="submit"
                    disabled={!newConvInput.trim() || creatingTicket}
                    className="flex-shrink-0 p-2.5 rounded-xl bg-green-700 text-white hover:bg-green-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {creatingTicket
                      ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      : <Send size={16} />}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
                <MessageSquare size={24} className="opacity-40" />
              </div>
              <p className="text-sm font-medium text-gray-500">Sélectionnez un ticket</p>
              <p className="text-xs text-gray-400 mt-1">Cliquez sur un ticket pour voir la conversation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
